// @flow
import React from "react";
import Transition from "./transition.js";
import mapKey, {OUT} from "../components/mapkey.js";
import {reisy, use} from "reisy";

const styles = reisy`
@namespace Navigation;

DropdownItem {
	flex: 0 0 auto;
	position: relative;
	pointer-events: all;
}

DropdownItemOpen {
	@extends DropdownItem;
	
}

Dropdown {
	position: absolute;
	z-index: 6;
	margin: 0px 4px;
	box-shadow: 0 8px 10px 1px rgba(0, 0, 0, 0.14),
	            0 3px 14px 2px rgba(0, 0, 0, 0.12),
	            0 5px 5px -3px rgba(0, 0, 0, 0.2);
	background: white;
	outline: none;
	line-height: 32px;
	/* XXX: hiding overflow breaks the arror done via ::before, but otherwise we
	 * would not get the proper border-radius. So add a small padding here so
	 * that content is offset to leave the border-radius visible */
	/*overflow: hidden;*/
	border-radius: 3px;
	padding: 3px 0px;
	&::before {
		content: "";
		position: absolute;
		width: 0;
		height: 0;
		top: -16px;
		border: 8px solid rgba(255, 255, 255, 0);
		border-bottom-color: white;
	}
	&.enter {
		opacity: 0;
		transform: translateY(8px);
	}
	&.enter-active {
		opacity: 1;
		transform: none;
		transition: all $Consts.Transition;
	},
	&.leave-active {
		opacity: 0;
		transform: translateY(8px);
		transition: all $Consts.Transition;
	}
}

DropdownRight {
	right: 0;
	&::before {
		right: 12px;
	}
}
DropdownLeft {
	left: 0;
	&::before {
		left: 12px;
	}
}
`;

type Props = {
	open?: boolean;
	anchor?: "Right" | "Left";
	item?: string;
	button: React.Element<*>;
	children: React.Element<*>;
};

class DropdownItem extends React.Component {
	// $FlowFixMe: https://github.com/facebook/flow/issues/1964
	props: Props;
	state: {open: boolean};

	elem: HTMLElement;

	setElem = (el: HTMLElement) => {
		this.elem = el;
		
		this._adjustAnchor();
	}

	constructor(props: Props) {
		super(props);
		this.state = {
			open: props.open || false,
			anchor: props.anchor || 'Right'
		};
	}

	handleClick = (e) => {
		if (e && e.touches) {
			e.preventDefault();
		}
		this.setState({
			open: !this.state.open,
		});
	};

	handleEnter = (e) => this.setState({
		open: true,
	});

	handleLeave = () => this.setState({
		open: false,
	});

	handleKeydown = (ev: KeyboardEvent) => {
		const key = mapKey(ev);
		if (OUT.includes(key) && this.state.open) {
			ev.stopPropagation();
			ev.preventDefault();
			this.setState({
				open: false,
			});
		}
	}

	handleDocClick = (ev: MouseEvent) => {
		const {elem, wrapper} = this;
		if (!elem || !this.state.open || wrapper.contains((ev.target: any))) { return; }
		this.setState({
			open: false,
		});
	}

	componentDidUpdate(prevProps, prevState) {
		if (this.state.open !== prevState.open) {
			this._adjustAnchor();
			this.props.onChange && this.props.onChange(this.state.open);
		}
		if (this.state.open) {
			document.addEventListener('click', this.handleDocClick);
			document.addEventListener('touchstart', this.handleDocClick);
			const {elem} = this;
			if (elem) {
				// XXX: FUCK! in containment mode, this focus scrolls the page to the
				// very top, WTF?
				const scroll = window.pageYOffset;
				elem.focus();
				window.scrollTo(0, scroll);
				elem.addEventListener('keydown', this.handleKeydown);
			}
		} else {
			this.componentWillUnmount();
		}
	}

	componentWillUnmount() {
		document.removeEventListener('click', this.handleDocClick);
		document.removeEventListener('touchstart', this.handleDocClick);
		const {elem} = this;
		if (elem) {
			elem.removeEventListener('keydown', this.handleKeydown);
		}
	}

	_adjustAnchor() {
		if (!this.elem) {
			return
		}

		const rect = this.elem.getBoundingClientRect();
		let anchor = this.state.anchor;
		
		if (rect.width >= window.innerWidth) {
			// prevent looping if the dropdown can't fit.
			return
		}

		if (anchor === 'Right' && rect.left < 0) {
			anchor = 'Left';
		} else if (anchor === 'Left' && rect.right > window.innerWidth) {
			anchor = 'Right';
		}

		if(anchor !== this.state.anchor) {
			this.setState({ anchor });
		}
	}
 
	render() {
		const {button, item, children} = this.props;
		const {open, anchor = 'Right'} = this.state;
		const props = this.props.openOnHover ? {onMouseLeave: this.handleLeave} : {};

		return (
			<div data-navbar-item={item} {...use(open ? styles.DropdownItemOpen : styles.DropdownItem, this.props.className)} ref={ref => this.wrapper = ref} {...props}>
				{React.cloneElement(button, this.props.openOnHover ? {onMouseEnter: this.handleEnter, onTouchStart: this.handleClick} : {onClick: this.handleClick})}
				<Transition
					element="div" _ref={this.setElem} render={open} renderHidden={this.props.renderHidden}
					tabIndex="0" {...use(styles.Dropdown, styles[`Dropdown${anchor}`])}>
					{React.cloneElement(children, typeof children.type === "string" ? null : {onClose: this.handleClick})}
				</Transition>
			</div>
		);
	}
}

export default DropdownItem;



// WEBPACK FOOTER //
// ./lib/navigation/dropdown.js